import React, { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2, XCircle, CreditCard } from 'lucide-react';
import { oceanpayApi, paymentApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { goToLogin } from '../utils/authRedirect';

// OceanPayment 嵌入式支付测试页（信用卡 / Google Pay）
// 流程：创建订单（后端生成签名）→ 按支付方式加载对应 JS SDK → 沙盒模式渲染支付组件 → 支付
// 结果：backUrl 后端处理并跳回本页（?orderNo=xx&result=xx），页面轮询订单状态确认入账
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('加载脚本失败: ' + src));
    document.head.appendChild(s);
  });
}

export default function TestOceanPay() {
  const { user, loading: authLoading } = useAuth();
  const [amount, setAmount] = useState('0.01');
  const [method, setMethod] = useState('Credit Card'); // 'Credit Card' | 'Google Pay'
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [gpReady, setGpReady] = useState(false); // Google Pay 按钮就绪标记
  const [orderNo, setOrderNo] = useState('');
  const [status, setStatus] = useState('idle'); // idle, pending, success, failed
  const [error, setError] = useState('');
  const [mrMessage, setMrMessage] = useState('');
  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const gpInitializedRef = useRef(false);

  // 未登录跳转登录
  useEffect(() => {
    if (!authLoading && !user) {
      goToLogin('/test-oceanpay');
    }
  }, [user, authLoading]);

  // backUrl 跳回本页时携带 orderNo/result，恢复订单状态并继续轮询
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const backOrderNo = params.get('orderNo');
    const result = params.get('result');
    if (backOrderNo && !orderNo) {
      setOrderNo(backOrderNo);
      setStatus('pending');
      if (result === 'failed') {
        setError('支付未成功（回调返回失败），可等待异步通知或重新发起支付');
      }
      if (result === 'success') {
        setStatus('success');
      }
      // 清理 URL 参数，避免刷新时重复触发
      window.history.replaceState({}, '', '/test-oceanpay');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 轮询订单状态（支付结果以异步通知为准，轮询用于页面展示）
  useEffect(() => {
    let intervalId;
    if ((status === 'pending' || status === 'success') && orderNo) {
      const check = async () => {
        try {
          const res = await paymentApi.checkStatus(orderNo);
          if (res.data && res.data.isPaid) {
            setStatus('success');
            setMrMessage(res.data.message || '');
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error('Check status error:', err);
        }
      };
      check();
      intervalId = setInterval(check, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status, orderNo]);

  const handleCreate = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setError('请输入有效的金额');
      return;
    }

    setError('');
    setMrMessage('');
    setLoading(true);
    setOrderNo('');
    setStatus('idle');
    setPaying(false);
    setGpReady(false);
    initializedRef.current = false;
    gpInitializedRef.current = false;

    try {
      const res = await oceanpayApi.create({ amount: Number(amount), method });

      if (res.data.success) {
        setOrderNo(res.data.orderNo);
        setStatus('pending');
        window.__oceanpayCheckoutParams = res.data.checkoutParams;
        window.__oceanpaySdkScripts = res.data.sdkScripts;
      } else {
        setError(res.data.error || '创建支付订单失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  // 用户填完卡信息后点击「确认支付」：此时沙盒 iframe 已加载完成，checkout 的 postMessage 才能被接收
  const handlePayNow = () => {
    if (!window.Oceanpayment || !window.__oceanpayCheckoutParams) {
      setError('支付组件未就绪，请刷新页面重试');
      return;
    }
    setError('');
    setPaying(true);
    try {
      window.Oceanpayment.checkout(window.__oceanpayCheckoutParams);
    } catch (err) {
      setPaying(false);
      setError('提交支付失败: ' + (err.message || err));
    }
  };

  // 信用卡：订单创建后加载 jQuery + OceanPayment SDK → 沙盒初始化 → 渲染卡表单（提交由「确认支付」按钮触发）
  useEffect(() => {
    if (status !== 'pending' || method !== 'Credit Card' || !window.__oceanpayCheckoutParams || initializedRef.current) return;

    let cancelled = false;

    const bootSdk = async () => {
      try {
        const scripts = window.__oceanpaySdkScripts || {};
        if (!window.jQuery) {
          await loadScript(scripts.jquery || 'https://secure.oceanpayment.com/pub/js/jquery/jq.js');
        }
        // SDK 只需加载一次，避免重复注册 message 监听导致回调触发多次
        if (!window.Oceanpayment) {
          await loadScript(scripts.oceanpayment || 'https://secure.oceanpayment.com/pages/js/oceanpayment.js');
        }

        if (cancelled) return;

        // 支付回调：data 可能是卡信息校验错误对象（data.msg）或支付结果 XML 字符串
        window.oceanpaymentCallBack = function (data) {
          console.log('OceanPayment CallBack:', data);
          if (data && data.msg) {
            setPaying(false);
            setError('支付错误: ' + data.msg);
            return;
          }
          const raw = typeof data === 'string' ? data : data && data.xml ? data.xml : '';
          if (!raw) return;
          try {
            const xml = new DOMParser().parseFromString(raw, 'text/xml');
            const getTag = (tag) => {
              const els = xml.getElementsByTagName(tag);
              return els.length > 0 ? (els[0].textContent || '') : '';
            };
            const payUrl = getTag('pay_url');
            if (payUrl) {
              // 3D 校验场景：跳转到 pay_url 完成 3D 校验，最终结果会再次送回 backUrl
              window.location.href = payUrl;
              return;
            }
            const payStatus = getTag('payment_status');
            const payDetails = getTag('payment_details');
            if (payStatus === '1') {
              setStatus('success');
            } else if (payStatus) {
              setPaying(false);
              setError('支付未成功: ' + payDetails);
              setStatus('failed');
            }
          } catch (err) {
            console.error('解析 OceanPayment 返回 XML 失败:', err);
          }
        };

        // 第一个参数 true 提交沙盒环境；语言默认 en_US
        // 注意：不能 init 后立即 checkout——官方流程要求 iframe 加载完成、用户填完卡信息后，
        // 再点击页面上的支付按钮调用 checkout 提交；否则 postMessage 目标源不匹配且卡信息为空
        window.Oceanpayment.init(true, '', 'en_US', { showCardName: true });

        initializedRef.current = true;
      } catch (err) {
        console.error('OceanPayment SDK 初始化失败:', err);
        if (!cancelled) setError('支付组件加载失败: ' + (err.message || err));
      }
    };

    // 等待 React 渲染出支付容器后再初始化 SDK
    const timer = setTimeout(() => {
      if (containerRef.current && !cancelled) bootSdk();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, method]);

  // Google Pay：订单创建后加载 jQuery + Google Pay 专用 SDK（onePageGooglePay）→ 沙盒初始化并渲染按钮
  // 官方流程：回调 code=2 表示按钮加载成功，此时提交订单参数（checkout）；code=3 为用户关闭支付弹窗；其余为下单返回
  useEffect(() => {
    if (status !== 'pending' || method !== 'Google Pay' || !window.__oceanpayCheckoutParams || gpInitializedRef.current) return;

    let cancelled = false;

    const bootGooglePay = async () => {
      try {
        const scripts = window.__oceanpaySdkScripts || {};
        if (!window.jQuery) {
          await loadScript(scripts.jquery || 'https://secure.oceanpayment.com/pub/js/jquery/jq.js');
        }
        if (!window.onePageGooglePay) {
          await loadScript(scripts.googlepay || 'https://secure.oceanpayment.com/pages/js/oceanpayment-googlepay.js');
        }

        if (cancelled) return;

        window.oceanpaymentGooglePayCallBack = function (data) {
          console.log('OceanPayment GooglePay CallBack:', data);
          // code=2：Google Pay 按钮加载成功，提交订单参数给网关
          if (data && data.code === 2) {
            setGpReady(true);
            try {
              window.onePageGooglePay.checkout(window.__oceanpayCheckoutParams);
            } catch (err) {
              setError('提交支付失败: ' + (err.message || err));
            }
            return;
          }
          // code=3：用户关闭了 Google Pay 支付弹窗
          if (data && data.code === 3) {
            setError('已取消 Google Pay 支付，可重新发起');
            return;
          }
          // 校验错误对象
          if (data && data.msg) {
            setError('支付错误: ' + data.msg);
            return;
          }
          // 下单返回（XML 字符串）：与信用卡链路一致地处理 pay_url / payment_status
          const raw = typeof data === 'string' ? data : data && data.xml ? data.xml : '';
          if (!raw) return;
          try {
            const xml = new DOMParser().parseFromString(raw, 'text/xml');
            const getTag = (tag) => {
              const els = xml.getElementsByTagName(tag);
              return els.length > 0 ? (els[0].textContent || '') : '';
            };
            const payUrl = getTag('pay_url');
            if (payUrl) {
              window.location.href = payUrl;
              return;
            }
            const payStatus = getTag('payment_status');
            const payDetails = getTag('payment_details');
            if (payStatus === '1') {
              setStatus('success');
            } else if (payStatus) {
              setError('支付未成功: ' + payDetails);
              setStatus('failed');
            }
          } catch (err) {
            console.error('解析 OceanPayment Google Pay 返回 XML 失败:', err);
          }
        };

        const params = window.__oceanpayCheckoutParams || {};
        // language 为 SDK 期望字段（拼接 iframe 地址用）；googleParam 不传时沙盒自动使用 OceanPayment 测试配置
        window.onePageGooglePay.init(true, {
          cssUrl: '',
          language: 'en_US',
          transactionInfo: {
            orderCurrency: params.order_currency || 'USD',
            orderAmount: params.order_amount || amount,
            billCountry: 'US'
          },
          buttonStyle: {
            buttonColor: '',
            buttonType: '',
            buttonRadius: '',
            buttonSizeMode: '',
            buttonLocale: ''
          }
        });

        gpInitializedRef.current = true;
      } catch (err) {
        console.error('OceanPayment Google Pay SDK 初始化失败:', err);
        if (!cancelled) setError('Google Pay 组件加载失败: ' + (err.message || err));
      }
    };

    const timer = setTimeout(() => {
      if (containerRef.current && !cancelled) bootGooglePay();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, method]);

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-blue-50/50 dark:bg-blue-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            OceanPayment {method === 'Google Pay' ? 'Google Pay' : '信用卡'}支付测试
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {method === 'Google Pay'
              ? '沙盒环境 · 需浏览器已登录 Google 账号方可完成支付'
              : '沙盒环境 · 测试卡 4111 1111 1111 1111 · 有效期 12/30 · CVV 123'}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* 支付方式选择 */}
          {status === 'idle' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                支付方式
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['Credit Card', 'Google Pay'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={`py-2 rounded-xl border text-sm font-medium transition-colors ${
                      method === m
                        ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-300'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 金额输入 */}
          {status === 'idle' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                测试支付金额 (USD，0.01-10)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0.01"
                  max="10"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {/* 发起支付按钮 */}
          {status === 'idle' && (
            <button
              onClick={handleCreate}
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> 创建订单中...</>
              ) : (
                '发起支付'
              )}
            </button>
          )}

          {/* 订单信息 + 支付表单容器 */}
          {(status === 'pending' || status === 'success') && orderNo && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 text-sm">
                <div className="text-gray-500 dark:text-gray-400">订单号</div>
                <div className="font-mono text-gray-900 dark:text-white break-all">{orderNo}</div>
              </div>

              {status === 'pending' && (
                method === 'Google Pay' ? (
                  <>
                    <div ref={containerRef} id="oceanpayment-googlepayelement"></div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {gpReady
                        ? 'Google Pay 按钮已就绪，点击按钮完成支付'
                        : 'Google Pay 组件加载中…'}
                    </p>
                  </>
                ) : (
                  <>
                    <div ref={containerRef} id="oceanpayment-element"></div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      填写卡信息后点击「确认支付」提交订单
                    </p>
                    <button
                      onClick={handlePayNow}
                      disabled={paying}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {paying ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> 支付处理中...</>
                      ) : (
                        '确认支付'
                      )}
                    </button>
                  </>
                )
              )}
            </div>
          )}

          {/* 支付成功 */}
          {status === 'success' && (
            <div className="flex flex-col items-center justify-center p-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400 mb-3" />
              <div className="text-lg font-semibold text-green-700 dark:text-green-300">支付成功，余额已入账</div>
              {mrMessage && (
                <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">{mrMessage}</div>
              )}
              <button
                onClick={() => {
                  setOrderNo('');
                  setStatus('idle');
                  setError('');
                }}
                className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                再次测试
              </button>
            </div>
          )}

          {/* 支付失败 */}
          {status === 'failed' && (
            <div className="flex flex-col items-center justify-center p-6 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
              <XCircle className="w-12 h-12 text-red-600 dark:text-red-400 mb-3" />
              <div className="text-lg font-semibold text-red-700 dark:text-red-300">支付未成功</div>
              <button
                onClick={() => {
                  setOrderNo('');
                  setStatus('idle');
                  setError('');
                }}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                重新测试
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
